import { useCallback, useEffect, useState } from 'react';
import { usePromptService } from '../../prompt/services/promptService';
import { useInvestigationService } from '../services/investigationService';
import type { PlaybookChoice } from '../components/CaseControls';

interface Options {
  customerId: string | undefined;
  investigationId: string | undefined;
  /** Refetches the investigation, so the chosen playbook shows without a reload. */
  onChanged: (investigationId: string) => void | Promise<void>;
}

/**
 * The playbooks an operator can put a case on, and the act of putting it on one.
 *
 * A playbook used to be choosable only in the dialog that creates a case, which is the dialog
 * operators skip: by the time a case showed it needed one, the only way to attach it was gone.
 * This is that choice, available for as long as the case is open.
 */
export const useCasePlaybook = ({ customerId, investigationId, onChanged }: Options) => {
  const promptService = usePromptService();
  const investigationService = useInvestigationService();
  const [playbooks, setPlaybooks] = useState<PlaybookChoice[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!customerId) return;
    let live = true;
    promptService
      .getAllPrompts(customerId)
      .then((prompts) => {
        if (!live) return;
        setPlaybooks(
          prompts
            .filter((prompt) => prompt.type === 'playbook')
            .map((prompt) => ({
              prompt_id: prompt.id,
              title: prompt.title,
              description: prompt.description,
              version: prompt.promptVersion ?? '',
              owner: prompt.owner,
            })),
        );
      })
      // A listing that failed is not an empty catalogue: saying so stops "No playbooks" standing
      // in for an unknown (PRIN-06).
      .catch((e) => live && setError(e instanceof Error ? e.message : 'Could not load playbooks'));
    return () => {
      live = false;
    };
  }, [customerId, promptService]);

  const choose = useCallback(
    async (promptId: string | null) => {
      if (!customerId || !investigationId) return;
      setError(null);
      const choice = promptId ? playbooks.find((p) => p.prompt_id === promptId) : null;
      try {
        // Inside the try: thrown outside it this was an unhandled rejection with nothing on
        // screen, so a stale list looked like a click that did nothing.
        if (promptId && !choice) {
          throw new Error('That playbook is no longer in the catalogue');
        }
        if (choice && !choice.version) {
          throw new Error(`${choice.title} has no published version to follow`);
        }
        await investigationService.setPlaybook(
          customerId,
          investigationId,
          choice ? { id: choice.prompt_id, version: choice.version, owner: choice.owner } : null,
        );
        await onChanged(investigationId);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'That playbook did not attach');
      }
    },
    [customerId, investigationId, playbooks, investigationService, onChanged],
  );

  // Memoised: the dashboard's snackbar close handler depends on it.
  const clearError = useCallback(() => setError(null), []);

  return { playbooks, choose, error, clearError };
};
